import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, type CaseRow, type Invoice } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, Money, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Saga orchestration (part 88).
 *
 * A saga is a business transaction made of ordered local steps. This page deliberately separates
 * starting a plan from advancing it: "start" only persists the plan, so an operator can see what
 * will run before any effect happens. "Advance" runs the plan to settlement and shows the step
 * states that resulted, including the reverse compensation order after a failure.
 *
 * The default amount is above the scenario's approval limit to make the failure drill obvious:
 * without an approval reference, step 3 refuses and steps 2 and 1 are compensated newest-first.
 */

type SagaStatus = 'running' | 'compensating' | 'completed' | 'compensated' | 'failed'
type StepStatus = 'pending' | 'running' | 'completed' | 'failed' | 'compensated' | 'compensation_failed' | 'skipped'

interface SagaRow {
  id: string
  code: string
  scenario: string
  title: string
  status: SagaStatus
  total_steps: number
  completed_steps: number
  current_sequence: number
  amount_satang?: number
  invoice_id?: string
  invoice_number?: string
  case_id?: string
  case_number?: string
  case_title?: string
  failure_step_key?: string
  failure_reason?: string
  approval_ref?: string
  started_at: string
  finished_at?: string
  updated_at: string
  reference_label?: string
  steps_completed?: number
  steps_compensated?: number
  steps_failed?: number
  compensation_count?: number
}

interface SagaStep {
  id: string
  saga_id: string
  sequence: number
  step_key: string
  title: string
  action_key: string
  compensation_key: string
  status: StepStatus
  attempts: number
  max_attempts: number
  compensation_attempts: number
  result?: string
  last_error?: string
  started_at?: string
  finished_at?: string
  compensated_at?: string
  updated_at: string
}

interface SagaCompensation {
  id: string
  saga_id: string
  step_id: string
  step_sequence: number
  step_key: string
  compensation_key: string
  status: 'succeeded' | 'failed'
  attempt: number
  effect?: string
  last_error?: string
  executed_at: string
}

interface SagaDetail extends SagaRow {
  steps: SagaStep[]
  compensations: SagaCompensation[]
  can_advance: boolean
  next_action: string
  reference_label?: string
}

interface ScenarioStep {
  key: string
  title: string
  action_key: string
  compensation_key: string
  max_attempts: number
}

interface Scenario {
  key: string
  title: string
  approval_limit_satang: number
  steps: ScenarioStep[]
}

const LIST_KEY = ['sagas'] as const
const detailKey = (id: string) => ['saga', id] as const

const statusLabel: Record<SagaStatus, string> = {
  running: 'กำลังดำเนินการ',
  compensating: 'กำลังชดเชย',
  completed: 'สำเร็จครบทุกขั้น',
  compensated: 'ชดเชยครบแล้ว',
  failed: 'ล้มเหลว',
}

const statusTone: Record<SagaStatus, string> = {
  running: 'sev-medium',
  compensating: 'sev-high',
  completed: 'sev-low',
  compensated: 'sev-low',
  failed: 'sev-critical',
}

const statusDetail: Record<SagaStatus, string> = {
  running: 'กำลังทำขั้นตอนถัดไปตามลำดับ ยังไม่มีการชดเชย',
  compensating: 'มีขั้นตอนล้มเหลว ระบบกำลังย้อนกลับขั้นตอนที่สำเร็จแล้วจากใหม่ไปเก่า',
  completed: 'ทุกขั้นตอนสำเร็จ การเปลี่ยนแปลงทั้งหมดถูกยืนยันครบ',
  compensated: 'ขั้นตอนที่สำเร็จก่อนจุดล้มเหลวถูกย้อนกลับครบแล้ว ธุรกรรมสิ้นสุดแบบปลอดภัย',
  failed: 'ไม่มีอะไรให้ชดเชย หรือการชดเชยเองล้มเหลว ต้องให้ผู้ดูแลตรวจสอบ',
}

const stepLabel: Record<StepStatus, string> = {
  pending: 'รอดำเนินการ',
  running: 'กำลังทำ',
  completed: 'สำเร็จ',
  failed: 'ล้มเหลว',
  compensated: 'ชดเชยแล้ว',
  compensation_failed: 'ชดเชยไม่สำเร็จ',
  skipped: 'ข้าม',
}

const stepTone: Record<StepStatus, string> = {
  pending: 'dim',
  running: 'sev-medium',
  completed: 'sev-low',
  failed: 'sev-critical',
  compensated: 'sev-high',
  compensation_failed: 'sev-critical',
  skipped: 'dim',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function amountFromBaht(raw: string): number {
  const baht = Number(raw.replace(/,/g, ''))
  if (!Number.isFinite(baht) || baht <= 0) return 0
  return Math.round(baht * 100)
}

export function Sagas() {
  const qc = useQueryClient()
  const { me } = useAuth()
  const canRun = me ? me.role !== 'reviewer' && me.role !== 'client_viewer' : false

  const [selectedID, setSelectedID] = useState<string | null>(null)
  const [invoiceID, setInvoiceID] = useState('')
  const [caseID, setCaseID] = useState('')
  const [amountBaht, setAmountBaht] = useState('75000.00')
  const [approvalRef, setApprovalRef] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  const list = useQuery({ queryKey: LIST_KEY, queryFn: () => api<SagaRow[]>('/v1/sagas?limit=100') })
  const scenarios = useQuery({
    queryKey: ['saga-scenarios'],
    queryFn: () => api<Scenario[]>('/v1/sagas/scenarios'),
    staleTime: 5 * 60_000,
  })
  const invoices = useQuery({
    queryKey: ['saga-invoices'],
    queryFn: () => api<Invoice[]>('/v1/invoices?limit=100'),
    staleTime: 60_000,
  })
  const cases = useQuery({
    queryKey: ['saga-cases'],
    queryFn: () => api<CaseRow[]>('/v1/cases?limit=100'),
    staleTime: 60_000,
  })
  const detail = useQuery({
    queryKey: selectedID ? detailKey(selectedID) : ['saga', 'none'],
    queryFn: async () => (await api<{ saga: SagaDetail }>(`/v1/sagas/${selectedID}`)).saga,
    enabled: selectedID !== null,
  })

  const scenario = scenarios.data?.[0]
  const approvalLimit = scenario?.approval_limit_satang ?? 5_000_000

  const rows = useMemo(() => list.data ?? [], [list.data])
  const counts = useMemo(() => {
    const out = { running: 0, compensating: 0, compensated: 0, failed: 0, completed: 0 }
    for (const row of rows) {
      if (row.status === 'running') out.running += 1
      else if (row.status === 'compensating') out.compensating += 1
      else if (row.status === 'compensated') out.compensated += 1
      else if (row.status === 'failed') out.failed += 1
      else if (row.status === 'completed') out.completed += 1
    }
    return out
  }, [rows])

  useEffect(() => {
    if (!selectedID && rows.length > 0) setSelectedID(rows[0].id)
  }, [rows, selectedID])

  useEffect(() => {
    if (!invoiceID && invoices.data && invoices.data.length > 0) setInvoiceID(invoices.data[0].id)
  }, [invoices.data, invoiceID])

  useEffect(() => {
    if (!caseID && cases.data && cases.data.length > 0) setCaseID(cases.data[0].id)
  }, [cases.data, caseID])

  const start = useMutation({
    mutationFn: (body: { scenario: string; invoice_id: string; case_id: string; amount_satang: number; approval_ref?: string }) =>
      apiWrite<{ saga: SagaDetail }>('/v1/sagas', 'POST', body),
    onSuccess: (res) => {
      setFormError(null)
      setSelectedID(res.saga.id)
      qc.setQueryData(detailKey(res.saga.id), res.saga)
      void qc.invalidateQueries({ queryKey: LIST_KEY })
      void qc.invalidateQueries({ queryKey: detailKey(res.saga.id) })
    },
    onError: (err) => setFormError(errorText(err, 'เริ่มซาก้าไม่สำเร็จ')),
  })

  const advance = useMutation({
    mutationFn: (id: string) => apiWrite<{ saga: SagaDetail; moves: number; advanced: boolean }>(
      `/v1/sagas/${id}/advance`, 'POST', {},
    ),
    onSuccess: (res) => {
      qc.setQueryData(detailKey(res.saga.id), res.saga)
      void qc.invalidateQueries({ queryKey: LIST_KEY })
    },
  })

  const selected = detail.data
  const amountSatang = amountFromBaht(amountBaht)
  const aboveLimit = amountSatang > approvalLimit

  function submit(event: FormEvent) {
    event.preventDefault()
    setFormError(null)
    if (!invoiceID) {
      setFormError('เลือกใบแจ้งหนี้ก่อนเริ่มซาก้า')
      return
    }
    if (!caseID) {
      setFormError('เลือกเคสตรวจสอบก่อนเริ่มซาก้า')
      return
    }
    if (amountSatang <= 0) {
      setFormError('ยอดปรับปรุงต้องมากกว่าศูนย์บาท')
      return
    }
    start.mutate({
      scenario: scenario?.key ?? 'billing_case',
      invoice_id: invoiceID,
      case_id: caseID,
      amount_satang: amountSatang,
      approval_ref: approvalRef.trim() || undefined,
    })
  }

  const advanceError = advance.error ? errorText(advance.error, 'เดินหน้าซาก้าไม่สำเร็จ') : null

  return (
    <div className="stack" style={{ gap: 18 }}>
      <PageHead
        title="ซาก้า"
        sub="ประสานธุรกรรมหลายขั้นตอนด้วยการชดเชยย้อนกลับ เมื่อขั้นตอนใดล้มเหลว ระบบจะย้อนขั้นตอนที่สำเร็จแล้วจากใหม่ไปเก่าและบันทึกทุกความพยายามไว้ตรวจสอบ"
      />

      <div className="grid-3">
        <Stat label="ซาก้าทั้งหมด" value={number(rows.length)} unit="รายการ" foot="เรียงจากใหม่ไปเก่า" />
        <Stat
          label="กำลังทำงาน"
          value={number(counts.running + counts.compensating)}
          unit="รายการ"
          foot={`กำลังชดเชย ${number(counts.compensating)} รายการ`}
        />
        <Stat
          label="ชดเชยครบแล้ว"
          value={number(counts.compensated)}
          unit="รายการ"
          foot={`สำเร็จครบทุกขั้น ${number(counts.completed)} รายการ`}
        />
        <Stat
          label="ล้มเหลว"
          value={number(counts.failed)}
          unit="รายการ"
          foot="ต้องให้ผู้ดูแลตรวจสอบด้วยมือ"
        />
      </div>

      <form className="card card-pad" onSubmit={submit}>
        <div className="row" style={{ gap: 10, marginBottom: 14 }}>
          <span className="card-label">เริ่มซาก้าใหม่ · billing_case</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            วงเงินอนุมัติ {amountLimitText(approvalLimit)}
          </span>
        </div>

        <div className="grid-2" style={{ gap: 14 }}>
          <label className="stack" style={{ gap: 6 }}>
            <span className="dim" style={{ fontSize: 11.5 }}>ใบแจ้งหนี้</span>
            <select className="input" value={invoiceID} onChange={(e) => setInvoiceID(e.target.value)}>
              <option value="">เลือกใบแจ้งหนี้</option>
              {(invoices.data ?? []).map((inv) => (
                <option key={inv.id} value={inv.id}>
                  {inv.number} · {inv.status} · {moneyText(inv.total_satang)}
                </option>
              ))}
            </select>
          </label>

          <label className="stack" style={{ gap: 6 }}>
            <span className="dim" style={{ fontSize: 11.5 }}>เคสตรวจสอบ</span>
            <select className="input" value={caseID} onChange={(e) => setCaseID(e.target.value)}>
              <option value="">เลือกเคส</option>
              {(cases.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.case_no} · {row.title}
                </option>
              ))}
            </select>
          </label>

          <label className="stack" style={{ gap: 6 }}>
            <span className="dim" style={{ fontSize: 11.5 }}>ยอดปรับปรุง (บาท)</span>
            <input
              className="input tnum"
              inputMode="decimal"
              value={amountBaht}
              onChange={(e) => setAmountBaht(e.target.value)}
              placeholder="75000.00"
            />
          </label>

          <label className="stack" style={{ gap: 6 }}>
            <span className="dim" style={{ fontSize: 11.5 }}>เลขอ้างอิงการอนุมัติ (ถ้ามียอดเกินวงเงิน)</span>
            <input
              className="input"
              value={approvalRef}
              onChange={(e) => setApprovalRef(e.target.value)}
              placeholder="APR-2026-0042"
              maxLength={120}
            />
          </label>
        </div>

        <div className="dim" style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.65 }}>
          {aboveLimit
            ? 'ยอดนี้เกินวงเงินอนุมัติ หากไม่ใส่เลขอ้างอิง ขั้นตอนที่ 3 จะถูกปฏิเสธและระบบจะชดเชยขั้นตอนที่ 2 และ 1 ตามลำดับย้อนกลับ'
            : 'ยอดนี้อยู่ภายในวงเงินอนุมัติ ขั้นตอนทั้งสี่จึงควรสำเร็จครบ'}
        </div>

        {formError ? (
          <div role="alert" className="dim" style={{ color: 'var(--color-crimson)', fontSize: 12, marginTop: 10 }}>
            {formError}
          </div>
        ) : null}

        <div className="row" style={{ marginTop: 14, gap: 10 }}>
          <button className="btn btn-primary" type="submit" disabled={!canRun || start.isPending}>
            {start.isPending ? 'กำลังสร้างซาก้า' : 'เริ่มซาก้า'}
          </button>
          <span className="dim" style={{ fontSize: 11 }}>
            การเริ่มยังไม่ทำงานใด ๆ ต้องกดเดินหน้าซาก้าแยกอีกครั้ง
          </span>
        </div>
      </form>

      <div className="card">
        <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
          <span className="card-label">ทะเบียนซาก้า</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            ทุกแถวอ่านจาก platform.sagas และ platform.saga_steps
          </span>
        </div>

        {list.isLoading ? (
          <div style={{ padding: 20 }} className="stack"><Skel h={48} /><Skel h={48} /><Skel h={48} /></div>
        ) : list.isError ? (
          <div style={{ padding: 20 }}>
            <Empty
              title="โหลดทะเบียนซาก้าไม่สำเร็จ"
              body={errorText(list.error, 'เรียก /v1/sagas ไม่สำเร็จ — ตรวจสอบว่าเส้นทางถูก mount แล้ว')}
            />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 20 }}>
            <Empty
              title="ยังไม่มีซาก้า"
              body="เริ่มซาก้าจากแบบฟอร์มด้านบน ระบบจะสร้างแถวใน platform.sagas พร้อมขั้นตอนที่รอดำเนินการโดยยังไม่ทำงานใด ๆ"
            />
          </div>
        ) : (
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>รหัส</th>
                  <th>อ้างอิง</th>
                  <th style={{ width: 170 }}>สถานะ</th>
                  <th style={{ width: 150 }}>ความคืบหน้า</th>
                  <th style={{ width: 140 }}>ยอดเงิน</th>
                  <th style={{ width: 180 }}>เริ่มเมื่อ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const active = row.id === selectedID
                  return (
                    <tr
                      key={row.id}
                      onClick={() => setSelectedID(row.id)}
                      style={{ cursor: 'pointer', background: active ? 'rgba(200, 169, 106, 0.05)' : undefined }}
                    >
                      <td>
                        <div className="mono" style={{ fontSize: 12, color: 'var(--color-ivory)' }}>{row.code}</div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>{row.title}</div>
                      </td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {row.reference_label ?? referenceLabelFromRow(row)}
                      </td>
                      <td>
                        <span className={`sev ${statusTone[row.status]}`}>
                          <i className="sev-dot" />
                          {statusLabel[row.status]}
                        </span>
                      </td>
                      <td className="tnum">
                        {number(row.completed_steps)} / {number(row.total_steps)}
                        <span className="dim"> ขั้น</span>
                      </td>
                      <td className="tnum">
                        {row.amount_satang === undefined ? '—' : <Money satang={row.amount_satang} />}
                      </td>
                      <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(row.started_at)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedID ? (
        <div className="card card-pad">
          <div className="row" style={{ gap: 10, marginBottom: 14 }}>
            <span className="card-label">รายละเอียดซาก้า</span>
            <span className="dim mono" style={{ marginLeft: 'auto', fontSize: 11 }}>{selected?.code ?? selectedID}</span>
          </div>

          {detail.isLoading ? (
            <div className="stack"><Skel h={60} /><Skel h={140} /></div>
          ) : detail.isError ? (
            <Empty title="โหลดรายละเอียดไม่สำเร็จ" body={errorText(detail.error, 'เรียก /v1/sagas/{id} ไม่สำเร็จ')} />
          ) : selected ? (
            <>
              <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <span className={`sev ${statusTone[selected.status]}`}>
                  <i className="sev-dot" />
                  {statusLabel[selected.status]}
                </span>
                <span className="chip">{selected.scenario}</span>
                {selected.amount_satang !== undefined ? (
                  <span className="chip"><Money satang={selected.amount_satang} /></span>
                ) : null}
                {selected.approval_ref ? <span className="chip">อนุมัติ {selected.approval_ref}</span> : null}
              </div>

              <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.7, marginBottom: 12 }}>
                {statusDetail[selected.status]}
                {selected.failure_step_key ? (
                  <>
                    {' '}ขั้นที่ล้มเหลว: <span className="mono">{selected.failure_step_key}</span>
                    {selected.failure_reason ? ` — ${selected.failure_reason}` : ''}
                  </>
                ) : null}
              </div>

              <div className="row" style={{ gap: 10, marginBottom: 16 }}>
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={!canRun || !selected.can_advance || advance.isPending}
                  onClick={() => advance.mutate(selected.id)}
                >
                  {advance.isPending
                    ? 'กำลังเดินหน้า'
                    : selected.status === 'running'
                      ? 'เดินหน้าซาก้า'
                      : selected.status === 'compensating'
                        ? 'ชดเชยขั้นตอนถัดไป'
                        : 'สิ้นสุดแล้ว'}
                </button>
                <span className="dim" style={{ fontSize: 11 }}>
                  คำขอเดียวจะเดินหน้าจนจบหรือจนถึงสถานะสิ้นสุด และบันทึกทุกขั้นตอนที่เกิดขึ้น
                </span>
              </div>

              {advanceError ? (
                <div role="alert" style={{ color: 'var(--color-crimson)', fontSize: 12, marginBottom: 12 }}>
                  {advanceError}
                </div>
              ) : null}

              <div className="table-wrap" style={{ marginBottom: 18 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th style={{ width: 54 }}>ลำดับ</th>
                      <th>ขั้นตอน</th>
                      <th style={{ width: 140 }}>สถานะ</th>
                      <th style={{ width: 120 }}>พยายาม</th>
                      <th>ผลหรือข้อผิดพลาด</th>
                      <th style={{ width: 200 }}>การชดเชย</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.steps.map((step) => (
                      <tr key={step.id}>
                        <td className="tnum">{step.sequence}</td>
                        <td>
                          <div style={{ fontSize: 12.5 }}>{step.title}</div>
                          <div className="mono dim" style={{ fontSize: 10.5, marginTop: 3 }}>{step.action_key}</div>
                        </td>
                        <td>
                          <span className={`sev ${stepTone[step.status]}`}>
                            <i className="sev-dot" />
                            {stepLabel[step.status]}
                          </span>
                        </td>
                        <td className="tnum dim" style={{ fontSize: 11.5 }}>
                          {number(step.attempts)} / {number(step.max_attempts)}
                        </td>
                        <td style={{ fontSize: 11.5 }}>
                          {step.last_error ? (
                            <span style={{ color: 'var(--color-crimson)' }}>{step.last_error}</span>
                          ) : step.result ? (
                            <span className="dim">{step.result}</span>
                          ) : (
                            <span className="dim">—</span>
                          )}
                        </td>
                        <td>
                          <div className="mono dim" style={{ fontSize: 10.5 }}>{step.compensation_key}</div>
                          <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                            {step.compensated_at
                              ? `ชดเชยเมื่อ ${thaiDateTime(step.compensated_at)}`
                              : step.compensation_attempts > 0
                                ? `ลองชดเชย ${number(step.compensation_attempts)} ครั้ง`
                                : 'ยังไม่ต้องชดเชย'}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="card-label" style={{ marginBottom: 10 }}>ประวัติการชดเชย</div>
              {selected.compensations.length === 0 ? (
                <div className="dim" style={{ fontSize: 12 }}>
                  ยังไม่มีการชดเชย ทุกความพยายามจะถูกบันทึกแบบเพิ่มอย่างเดียวใน platform.saga_compensations
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th style={{ width: 170 }}>เวลา</th>
                        <th style={{ width: 60 }}>ขั้น</th>
                        <th>ขั้นตอน</th>
                        <th style={{ width: 90 }}>ครั้งที่</th>
                        <th style={{ width: 120 }}>ผล</th>
                        <th>บันทึก</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.compensations.map((comp) => (
                        <tr key={comp.id}>
                          <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(comp.executed_at)}</td>
                          <td className="tnum">{comp.step_sequence}</td>
                          <td>
                            <div style={{ fontSize: 12 }}>{comp.step_key}</div>
                            <div className="mono dim" style={{ fontSize: 10.5, marginTop: 3 }}>{comp.compensation_key}</div>
                          </td>
                          <td className="tnum">{comp.attempt}</td>
                          <td>
                            <span className={`sev ${comp.status === 'succeeded' ? 'sev-low' : 'sev-critical'}`}>
                              <i className="sev-dot" />
                              {comp.status === 'succeeded' ? 'สำเร็จ' : 'ล้มเหลว'}
                            </span>
                          </td>
                          <td style={{ fontSize: 11.5 }}>
                            {comp.last_error ? (
                              <span style={{ color: 'var(--color-crimson)' }}>{comp.last_error}</span>
                            ) : (
                              <span className="dim">{comp.effect ?? '—'}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : null}
        </div>
      ) : null}

      {scenario ? (
        <div className="card card-pad">
          <div className="card-label" style={{ marginBottom: 10 }}>ขั้นตอนของสถานการณ์ {scenario.key}</div>
          <ol className="stack" style={{ gap: 8, margin: 0, paddingLeft: 20 }}>
            {scenario.steps.map((step) => (
              <li key={step.key} style={{ fontSize: 12.5, lineHeight: 1.65 }}>
                <span>{step.title}</span>
                <span className="dim mono" style={{ fontSize: 10.5, marginLeft: 8 }}>
                  {step.action_key} → {step.compensation_key}
                </span>
                <span className="dim" style={{ fontSize: 10.5, marginLeft: 8 }}>
                  ลองได้สูงสุด {number(step.max_attempts)} ครั้ง
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  )
}

function referenceLabelFromRow(row: SagaRow): string {
  if (row.invoice_number && row.case_number) return `${row.invoice_number} · ${row.case_number}`
  return row.invoice_number || row.case_number || '—'
}

function moneyText(satang: number): string {
  return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(satang / 100)
}

function amountLimitText(satang: number): string {
  return `฿${moneyText(satang)}`
}
